import React, { useEffect, useState } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { ButtonVariantType } from "../../../Common/UI/Types";
import { Button } from "../../../Common/UI/Index";
import { StatusTag } from "../../../Common/StatusTag/StatusTag";
import { ProgressSpinnerSlice } from "../../../ProgressSpinner/Index";
import { useExecutionConfiguration, useRunExecution } from "../../../../service/hooks";
import { ErrorAlertSlice } from "../../../ErrorAlert/Index";
import { setNotification } from "../../../../utilities/HttpRequest/Script";
import { IExecutionConfiguration, ScreenMode, STATUS_POLL, IError } from "./Types";
import { SummaryEntry } from "./SummaryEntry";
import { SummaryView } from "./SummaryView";
import RunTemplate from "~icons/ic/baseline-play-arrow";
import Edit from "~icons/ic/outline-edit";
import ChevronLeft from "~icons/ic/outline-chevron-left";
import "./Style.scss";

interface ExecutionDetailProps {
  onReturnToListClick: () => void;
  executionConfigurationId: string;
  mode: ScreenMode;
  setMode: (value: (((prevState: ScreenMode) => ScreenMode) | ScreenMode)) => void;
}

export const ExecutionDetail: React.FC<ExecutionDetailProps> = ({ onReturnToListClick, executionConfigurationId,
  mode, setMode }) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const abortController = new AbortController();
  const signal = abortController.signal;
  const queryClient = useQueryClient();
  const [executionConfiguration, setExecutionConfiguration] = useState<IExecutionConfiguration>();

  const executionRequest = useExecutionConfiguration(executionConfigurationId);

  const runExecutionRequest = useRunExecution({
    onSuccess: (data: { data: IExecutionConfiguration }) => {
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      queryClient.invalidateQueries({ queryKey: ["executionList"] });
      queryClient.invalidateQueries({ queryKey: ["executionConfiguration", executionConfigurationId] });
      const ecData: IExecutionConfiguration = data.data;
      if (ecData) {
        setExecutionConfiguration(structuredClone(ecData));
      }
      setNotification(dispatch, "run-execution", translate("Notification.RunExecutionSuccess"), "success");
    },
    onError: (error: IError) => {
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      dispatch(ErrorAlertSlice.actions.createErrorAlert({
        title: "Error",
        message: [error.errors[0].message || translate("UnknownError")],
        type: "error"
      }));
    },
  });

  const refetchData = (): void => {
    if (!signal.aborted) {
      executionRequest.refetch();
    }
  };

  const onEditClick = (): void => {
    setMode(ScreenMode.EDIT);
  };

  const onRunClick = (): void => {
    runExecutionRequest.mutate(executionConfigurationId);
  };

  const getExecutionConfiguration = (): void => {
    if (executionRequest.error) {
      dispatch(ErrorAlertSlice.actions.createErrorAlert({
        title: "Error",
        message: [executionRequest.error["errors"][0].message || translate("UnknownError")],
        type: "error"
      }));
    } else {
      const ecData: IExecutionConfiguration = executionRequest.data?.data as IExecutionConfiguration;
      if (ecData) {
        setExecutionConfiguration(structuredClone(ecData));
        if (ecData.status === "Running") {
          setTimeout(() => refetchData(), STATUS_POLL);
        }
      }
    }
  };

  useEffect(() => {
    if (!signal.aborted && executionRequest.isLoading) {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.ExecutionData")));
    }
  }, [executionRequest.isLoading]);

  useEffect(() => {
    if (!signal.aborted && !executionRequest.isLoading && !executionRequest.isRefetching) {
      void getExecutionConfiguration();
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
    }
  }, [executionRequest.isLoading, executionRequest.isRefetching]);


  React.useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <div className="add-execution">
      <div className="add-execution__label">
        <h2>
          {
            mode === ScreenMode.ADD
              ? `${translate("AddExecution.UntitledExecutionLabel")} ${translate("AddExecution.Summary")}`
              : `${executionConfiguration?.name || ""} ${translate("AddExecution.Summary")}`}
        </h2>
      </div>
      <div className="add-execution__top-buttons">
        <div className="add-execution__back-button">
          <Button
            data-testid="addExecutionBackButton"
            id="addExecutionBackButton"
            variant={ButtonVariantType.MONO}
            onClick={onReturnToListClick}>
            <ChevronLeft title="back"/>
          </Button>
        </div>
        <div className="add-execution__edit_run-container">
          {executionConfiguration && mode === ScreenMode.VIEW &&
                        <div className="status-container">
                            Status
                          <div style={{ marginTop: "-3px" }}><StatusTag displayText={executionConfiguration.status}/>
                          </div>
                        </div>
          }
          <div>
            <Button
              data-testid="addExecutionEditButton"
              id="addExecutionEditButton"
              onClick={onEditClick}
              disabled={!(mode === ScreenMode.VIEW && executionConfiguration?.status === "Draft")}
              className="add-execution__edit-button"
            >
              <Edit title={translate("Button.Edit")}/>
              {translate("Button.Edit")}
            </Button>
            <Button
              data-testid="addExecutionRunButton"
              id="addExecutionRunButton"
              onClick={onRunClick}
              disabled={!(mode === ScreenMode.VIEW && executionConfiguration?.status === "Draft")}
              variant={ButtonVariantType.FILLED}>
              <RunTemplate title={translate("Button.Run")}/>
              {translate("Button.Run")}
            </Button>
          </div>
        </div>
      </div>
      <div>
        {
          mode === ScreenMode.ADD &&
          <SummaryEntry mode={ScreenMode.ADD} setMode={setMode} onReturnToListClick={onReturnToListClick}/>
        }
        {
          executionConfiguration && mode === ScreenMode.EDIT &&
          <SummaryEntry
            mode={ScreenMode.EDIT}
            setMode={setMode}
            onReturnToListClick={onReturnToListClick}
            executionConfiguration={executionConfiguration}/>
        }
        {
          mode === ScreenMode.VIEW && executionConfiguration &&
          <SummaryView
            executionConfiguration={executionConfiguration}
            showingRunDates={true}
          />
        }
      </div>
    </div>
  );
};
